/**
 * Copyright (C) Firstlight Media.
 * All rights reserved.
 */

import { Logger } from '@quickplay/foundation';
import { Player } from '@quickplay/player-interface';
import datazoom from '@datazoom/collector_shaka_mt_om';

/**
 * Datazoom initialization configuration
 */
export interface DatazoomConfig {
  configId: string;
}

declare global {
  interface Window {
    shaka: any;
  }
}

/**
 * Datazoom Integration (Singleton)
 *
 * Manages lifecycle of the Datazoom JS Collector for
 * client-side MediaTailor + Shaka integrations.
 */
export class DatazoomFactory {
  private static initialized = false;
  private static sdk: typeof datazoom | null = null;
  private static logger?: Logger;

  /* -------------------------------------------------------------------------- */
  /* Initialization                                                             */
  /* -------------------------------------------------------------------------- */

  public static initialize(config: DatazoomConfig, logger?: Logger): void {
    if (this.initialized) {
      logger?.log('[Datazoom] Already initialized');
      return;
    }

    if (!config?.configId) {
      throw new Error('[Datazoom] configId is required');
    }

    this.logger = logger;
    this.sdk = datazoom;

    try {
      this.sdk.init({
        configuration_id: config.configId,
      });

      this.initialized = true;
      logger?.log('[Datazoom] Initialized', { configId: config.configId });
    } catch (error) {
      this.reset();
      logger?.error?.('[Datazoom] Initialization failed', error);
      throw error;
    }
  }

  /**
   * Returns true if SDK is initialized
   */
  public static isInitialized(): boolean {
    return this.initialized;
  }

  /* -------------------------------------------------------------------------- */
  /* Collector Context                                                          */
  /* -------------------------------------------------------------------------- */

  public static createCollectorContext(player: Player): any {
    if (!this.sdk) {
      this.logger?.error?.('[Datazoom] SDK not initialized');
      throw new Error('Datazoom SDK not initialized');
    }
    try {
      globalThis.window.shaka = player.rawLibrary;
      return this.sdk.createContext(player.rawPlayer);
    } catch (error) {
      this.logger?.error?.('[Datazoom] Failed to create collector context', error);
      throw error;
    }
  }

  /* -------------------------------------------------------------------------- */
  /* PAL (Privacy-Preserving Ad Technology)                                     */
  /* -------------------------------------------------------------------------- */

  static initPal(): void {
    if (!this.sdk || typeof this.sdk.mediatailor.initPal !== 'function') {
      this.logger?.warn?.('[Datazoom] PAL not supported or SDK not initialized');
      return;
    }

    try {
      const palConsentSettings = {
        allowStorage: false,
      };

      this.sdk.mediatailor.initPal(null, palConsentSettings);
      this.logger?.log('[Datazoom] PAL initialized');
    } catch (error) {
      this.logger?.error?.('[Datazoom] PAL initialization failed', error);
    }
  }

  /* -------------------------------------------------------------------------- */
  /* Utilities                                                                  */
  /* -------------------------------------------------------------------------- */

  /**
   * Set DataZoom internal log level
   */
  public static setLogLevel(level: 'off' | 'error' | 'warning' | 'info' | 'debug' | 'verbose'): void {
    if (!this.sdk || typeof this.sdk.setLogLevel !== 'function') {
      return;
    }

    try {
      this.sdk.setLogLevel(level);
      this.logger?.log('[Datazoom] Log level set', { level });
    } catch (error) {
      this.logger?.error?.('[Datazoom] Failed to set log level', error);
    }
  }

  /**
   * Returns Datazoom SDK version
   */
  public static getSDKVersion(): string {
    return this.sdk?.version ?? 'unknown';
  }

  /**
   * Open Measurement (OM SDK) partner info
   */
  public static getOMSDKPartnerInfo(): { name: string; version: string } | null {
    if (!this.sdk) {
      return null;
    }

    return {
      name: this.sdk.omidPartnerName,
      version: this.sdk.omidPartnerVersion,
    };
  }

  /**
   * Reset Datazoom state
   *
   * Intended for testing or full teardown
   */
  public static reset(): void {
    this.initialized = false;
    this.sdk = null;
    this.logger?.log('[Datazoom] Reset completed');
  }

  /**
   * Remove a previously created collector context
   */
  public static removeCollectorContext(context: any): void {
    try {
      // Some SDK builds expose removeContext; guard for availability
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.sdk?.removeContext?.(context);
      this.logger?.log('[Datazoom] Collector context removed');
    } catch (error) {
      this.logger?.error?.('[Datazoom] Failed to remove collector context', error);
    }
  }

  /**
   * Get raw SDK instance (advanced use only)
   */
  public static getSDK(): typeof datazoom | null {
    return this.sdk;
  }
}
